<template>
  <div class="flex justify-end">
    <Pagination
      :items-per-page="15"
      :total="pagination.totalItems"
      :default-page="pagination.page"
      v-slot="{ page: currentPage }"
    >
      <div class="flex items-center gap-1">
        <PaginationPrevious
          v-if="pagination.page > 1"
          href="#"
          @click.prevent="backPage()"
        />

        <PaginationContent>
          <PaginationItem
            v-for="page in pageNumbers"
            :key="page"
            :value="page"
            as-child
            :is-active="page === pagination.page"
          >
            <a
              href="#"
              @click.prevent="goToPage(page)"
            >
              {{ page }}
            </a>
          </PaginationItem>
        </PaginationContent>

        <PaginationNext
          v-if="pagination.page < pagination.totalPages"
          href="#"
          @click.prevent="nextPage()"
        />
      </div>
    </Pagination>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, reactive } from 'vue';

  import {
    Pagination,
    PaginationContent,
    PaginationItem,
    PaginationNext,
    PaginationPrevious,
  } from '@/components/ui/pagination';
  import type { ListPaginationInterface } from '@/interfaces/base/pagination/listPagination.interface';
  import type { PaginationInterface } from '@/interfaces/base/pagination/pagination.interface';

  const props = defineProps<{
    pagination: ListPaginationInterface;
  }>();

  const emit = defineEmits<{
    (e: 'paramsPagination', value: PaginationInterface): void;
  }>();

  const paramsPagination = reactive<PaginationInterface>({
    page: 1,
    pageSize: 15,
  });

  onMounted(() => {
    paramsPagination.page = props.pagination.page;
  });

  const nextPage = () => {
    paramsPagination.page = Number(props.pagination.page) + 1;
    emit('paramsPagination', paramsPagination);
  };

  const backPage = () => {
    paramsPagination.page = Number(props.pagination.page) - 1;
    emit('paramsPagination', paramsPagination);
  };

  const goToPage = (page: number) => {
    paramsPagination.page = page;
    emit('paramsPagination', paramsPagination);
  };

  const pageNumbers = computed(() => {
    const current = props.pagination.page;
    const total = props.pagination.totalPages;
    const pages: number[] = [];

    if (total <= 7) {
      for (let i = 1; i <= total; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (current <= 3) {
        pages.push(2, 3, 4);
      } else if (current >= total - 2) {
        pages.push(total - 3, total - 2, total - 1);
      } else {
        pages.push(current - 1, current, current + 1);
      }

      pages.push(total);
    }

    return pages;
  });
</script>
